import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import axios from "axios";
import CustomBreadcrumb from '../../common/CustomBreadcrumb'
import CampaignModal from '../../common/CampaignModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { Popover, message, Modal, Input } from "antd";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { useDispatch, useSelector } from "react-redux";
import {
	BtnBold,
	BtnBulletList,
	HtmlButton,
	BtnItalic,
	BtnNumberedList,
	BtnStrikeThrough,
	BtnUnderline,
	Editor,
	EditorProvider,
	Toolbar,
	createButton,
} from "react-simple-wysiwyg";
import {
	BtnAlignCenter,
	BtnAlignLeft,
	BtnAlignRight,
	BtnSubscript,
	BtnSuperscript,
} from "../Html_Editor/BtnFontFamily";
import infoicon from "../../assets/infoicon.svg";
import CsvUploadDragger from '../Dragger/CsvUploadDragger';
import { UploadCsvIcon, DeleteUploadIcon, InfoIcon } from '../../assets/icon';
import copyIcon from '../../assets/Copy.svg';
import './ManageTerms.css';
import {
	getTermsAndConditionsDetailsAction,
	getTermsAndConditionsDetailsHandler,
} from "../../redux/action/getTermsAndConditionsDetailsSlice";
import { updateTermsAndConditionsAction, updateTermsAndConditionsHandler } from "../../redux/action/updateTermsAndConditionsSlice";
import {
	deleteFileAction,
	deleteFileHandler,
} from "../../redux/action/deleteFileSlice";
import { ThemeContext } from '../../common/themeContext';
import { getItem } from "../../common/localStorage";
import { API_URL } from "../../services/http";
import Loader from "../Loder/loader";

const TERMS_PDF_UPLOAD_BASE = "campaign/terms-conditions";

function fileNameFromSignedUrl(url) {
	if (!url || typeof url !== "string") return "";
	try {
		const pathname = new URL(url).pathname;
		const segment = pathname.split("/").filter(Boolean).pop() || "document.pdf";
		return decodeURIComponent(segment);
	} catch {
		return "document.pdf";
	}
}

/** S3/object key style path expected by the update API (not the presigned query URL). */
function storagePathFromFileUrl(url) {
	if (!url || typeof url !== "string") return "";
	const trimmed = url.trim();
	if (!/^https?:\/\//i.test(trimmed)) return trimmed;
	try {
		const raw = new URL(trimmed).pathname.replace(/^\//, "");
		return raw
			.split("/")
			.map((part) => decodeURIComponent(part))
			.join("/");
	} catch {
		return "";
	}
}

/** Map `/file/upload` response to stored object key (`fileUrl`) and optional HTTPS URL (presigned or public). */
function normalizeS3UploadResult(uploadData) {
	const data = uploadData && typeof uploadData === "object" ? uploadData : {};
	const signed = data.signedUrl != null ? String(data.signedUrl) : "";
	const preSigned = data.preSignedUrl != null ? String(data.preSignedUrl) : "";
	const rawFileUrl = data.fileUrl != null ? String(data.fileUrl) : "";
	const genericUrl = data.url != null ? String(data.url) : "";
	const isHttp = (s) => /^https?:\/\//i.test((s || "").trim());

	let fileUrl = "";
	if (rawFileUrl && !isHttp(rawFileUrl)) fileUrl = rawFileUrl.trim();
	else if (rawFileUrl && isHttp(rawFileUrl)) fileUrl = storagePathFromFileUrl(rawFileUrl);
	else if (signed && !isHttp(signed)) fileUrl = signed.trim();
	else if (signed && isHttp(signed)) fileUrl = storagePathFromFileUrl(signed);
	else if (genericUrl && isHttp(genericUrl)) fileUrl = storagePathFromFileUrl(genericUrl);
	if (!fileUrl && preSigned && isHttp(preSigned)) {
		fileUrl = storagePathFromFileUrl(preSigned);
	}

	const url =
		(preSigned && isHttp(preSigned) ? preSigned : "") ||
		(signed && isHttp(signed) ? signed : "") ||
		(rawFileUrl && isHttp(rawFileUrl) ? rawFileUrl : "") ||
		(genericUrl && isHttp(genericUrl) ? genericUrl : "");

	return { fileUrl, url };
}

function buildDocumentPayload(documents) {
	const english = [];
	const spanish = [];
	for (const doc of documents) {
		const fileUrl =
			doc?.fileUrl ||
			(doc?.url ? storagePathFromFileUrl(doc.url) : "") ||
			(doc?.signedUrl ? storagePathFromFileUrl(doc.signedUrl) : "");
		if (!fileUrl) continue;
		let fileName = doc?.fileName ?? doc?.name ?? "";
		if (!fileName) {
			fileName =
				(doc?.signedUrl ? fileNameFromSignedUrl(doc.signedUrl) : "") ||
				(doc?.url ? fileNameFromSignedUrl(doc.url) : "") ||
				fileUrl.split("/").filter(Boolean).pop() ||
				"document.pdf";
		}
		const row = { fileName, fileUrl };
		const lang = doc?.lang === "spanish" ? "spanish" : "english";
		if (lang === "spanish") spanish.push(row);
		else english.push(row);
	}
	return { english, spanish };
}

/** Build local document rows from API `data.document` (per-language arrays with fileUrl + signedUrl). */
function documentsFromApiDetails(details) {
	const block = details?.document;
	if (!block || typeof block !== "object") return [];

	const out = [];
	const addList = (list, lang, prefix) => {
		if (!Array.isArray(list)) return;
		list.forEach((item, index) => {
			if (!item || typeof item !== "object") return;
			const rawKey = item.fileUrl != null ? String(item.fileUrl).trim() : "";
			const signed = item.signedUrl != null ? String(item.signedUrl).trim() : "";
			const fileUrl =
				rawKey && !/^https?:\/\//i.test(rawKey)
					? rawKey
					: signed
						? storagePathFromFileUrl(signed)
						: rawKey
							? storagePathFromFileUrl(rawKey)
							: "";
			if (!fileUrl) return;
			const fileName =
				item.fileName != null && String(item.fileName).trim()
					? String(item.fileName).trim()
					: signed
						? fileNameFromSignedUrl(signed)
						: fileUrl.split("/").filter(Boolean).pop() || "document.pdf";
			out.push({
				id: `${prefix}-${index}-${fileUrl}`,
				name: fileName,
				fileName,
				fileUrl,
				signedUrl: signed,
				url: signed,
				lang,
			});
		});
	};
	addList(block.english, "english", "api-en");
	addList(block.spanish, "spanish", "api-es");
	return out;
}

/** True when HTML is missing or only whitespace / empty block markup (e.g. `<p><br></p>`). */
function isHtmlContentEmpty(html) {
	if (html == null || typeof html !== "string") return true;
	const text = html
		.replace(/<[^>]*>/g, " ")
		.replace(/&nbsp;/gi, " ")
		.replace(/\u00a0/g, " ")
		.replace(/\s+/g, " ")
		.trim();
	return text.length === 0;
}

/** Object key / path for `file/delete` — same style as upload listing (not presigned URL). */
function filePathForDeleteApi(doc) {
	const raw = doc?.fileUrl != null ? String(doc.fileUrl).trim() : "";
	if (raw && !/^https?:\/\//i.test(raw)) return raw;
	if (doc?.signedUrl) return storagePathFromFileUrl(doc.signedUrl);
	if (doc?.url) return storagePathFromFileUrl(doc.url);
	return "";
}

const ManageTerms = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { brandId } = useContext(ThemeContext);
	const { state } = useLocation();
	const termsConditionId = state?.termsConditionId;
	const documentNameFromState = state?.documentName;
	const [activeKey, setActiveKey] = useState("text");
	const [infoPopoverVisible, setInfoPopoverVisible] = useState(false);
	const [manageSwitch, setManageSwitch] = useState(false);
	const [englishHtml, setEnglishHtml] = useState("");
	const [spanishHtml, setSpanishHtml] = useState("");
	const [documentName, setDocumentName] = useState(
		documentNameFromState || "Terms and Conditions v1",
	);
	const [documents, setDocuments] = useState([]);
	const [deleteModal, setDeleteModal] = useState({ open: false, docId: null });
	const [linkModalOpen, setLinkModalOpen] = useState(false);
	const [linkUrlInput, setLinkUrlInput] = useState("");
	const linkRangeRef = useRef(null);
	const linkEditorElRef = useRef(null);
	const linkNodeRef = useRef(null);

	const updateTermsAndConditionsState = useSelector(
		(state) => state?.updateTermsAndConditions,
	);
	const getTermsAndConditionsDetailsState = useSelector(
		(state) => state?.getTermsAndConditionsDetails,
	);
	const deleteFileState = useSelector((state) => state?.deleteFile);

	const currentHtml = useMemo(
		() => (!manageSwitch ? englishHtml : spanishHtml),
		[englishHtml, manageSwitch, spanishHtml],
	);

	const libraryDocuments = useMemo(
		() =>
			documents.filter((d) =>
				manageSwitch ? d.lang === "spanish" : d.lang === "english",
			),
		[documents, manageSwitch],
	);

	const onEditorChange = (e) => {
		const val = e?.target?.value ?? "";
		if (!manageSwitch) setEnglishHtml(val);
		else setSpanishHtml(val);
	};

	useEffect(() => {
		if (!termsConditionId) return;
		if (!brandId) return;

		dispatch(
			getTermsAndConditionsDetailsHandler({
				termsConditionId,
				refBrandId: brandId,
			}),
		);

		return () => {
			dispatch(
				getTermsAndConditionsDetailsAction.getTermsAndConditionsDetailsInfoReset(),
			);
		};
	}, [brandId, dispatch, termsConditionId]);

	useEffect(() => {
		const apiBody = getTermsAndConditionsDetailsState?.data;
		if (!apiBody) return;

		const details = apiBody?.data ?? apiBody;
		if (!details || typeof details !== "object") return;

		setDocumentName(
			details.documentName ||
			documentNameFromState ||
			"Terms and Conditions v1",
		);

		const content = details.content ?? {};
		setEnglishHtml(content.english ?? "");
		setSpanishHtml(content.spanish ?? "");

		setDocuments(documentsFromApiDetails(details));
	}, [getTermsAndConditionsDetailsState?.data, documentNameFromState]);

	useEffect(() => {
		const err = getTermsAndConditionsDetailsState?.error;
		if (!err) return;
		message.error(err);
	}, [getTermsAndConditionsDetailsState?.error]);

	const handleSave = async () => {
		try {
			if (isHtmlContentEmpty(englishHtml) || isHtmlContentEmpty(spanishHtml)) {
				message.error("Terms & Conditions content cannot be empty.");
				return;
			}
			const details =
				getTermsAndConditionsDetailsState?.data?.data ??
				getTermsAndConditionsDetailsState?.data ??
				{};
			const documentPayload = buildDocumentPayload(documents);
			const hasDocument =
				documentPayload.english.length > 0 ||
				documentPayload.spanish.length > 0;
			const payload = {
				refBrandId: brandId,
				termsConditionId: details?.id ?? termsConditionId,
				documentName,
				content: {
					english: englishHtml || "",
					spanish: spanishHtml || "",
				},
				document: hasDocument ? documentPayload : null,
			};
			dispatch(updateTermsAndConditionsHandler(payload));
		} catch (e) {
			message.error(
				e?.response?.data?.message || e?.message || "Failed to save",
			);
		}
	};

	const handleFileSelected = async (file) => {
		const selectedBrandId = brandId;
		const adminId = getItem("adminId");
		const langFolder = manageSwitch ? "Spanish" : "English";
		const formData = new FormData();
		formData.append("file", file);
		formData.append("id", Number(adminId));
		formData.append("brandId", selectedBrandId);
		formData.append("path", `${TERMS_PDF_UPLOAD_BASE}/${langFolder}`);

		let uploadData;
		let uploadMessage = "";
		try {
			const response = await axios.post(`${API_URL}/file/upload`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
					Authorization: `Bearer ${localStorage.getItem("token")}`,
				},
			});
			uploadData = response?.data?.data || response?.data || {};
			uploadMessage = response?.data?.message || "";
		} catch (uploadError) {
			const uploadErrorMessage =
				uploadError?.response?.data?.message ||
				uploadError?.message ||
				"File upload failed";
			message.error(uploadErrorMessage);
			return false;
		}

		const { fileUrl, url } = normalizeS3UploadResult(uploadData);
		if (!fileUrl) {
			message.error("Upload did not return a file location");
			return false;
		}

		const signedFromResponse =
			uploadData?.signedUrl != null &&
				/^https?:\/\//i.test(String(uploadData.signedUrl).trim())
				? String(uploadData.signedUrl).trim()
				: "";
		const copyUrl = signedFromResponse || url || "";

		const newDoc = {
			id: Date.now(),
			name: file.name,
			fileName: file.name,
			lang: manageSwitch ? "spanish" : "english",
			fileUrl,
			...(copyUrl
				? { signedUrl: copyUrl, url: copyUrl }
				: {}),
		};
		setDocuments((prev) => [newDoc, ...prev]);
		message.success(uploadMessage || `${file.name} uploaded successfully`);
		return true;
	};

	const closeDeleteModal = () => {
		setDeleteModal({ open: false, docId: null });
		dispatch(deleteFileAction.deleteFileInfoReset());
	};

	const handleDeleteDoc = async () => {
		const docId = deleteModal.docId;
		if (docId == null) return;
		const doc = documents.find((d) => d.id === docId);
		if (!doc) {
			message.error("File not found");
			closeDeleteModal();
			return;
		}
		const filePath = filePathForDeleteApi(doc);
		if (!filePath) {
			message.error("Could not resolve file path for deletion");
			return;
		}
		try {
			const res = await dispatch(deleteFileHandler({ filePath }));
			setDocuments((prev) => prev.filter((d) => d.id !== docId));
			message.success(res?.message || "Document removed from storage");
			closeDeleteModal();
		} catch (e) {
			message.error(
				typeof e === "string"
					? e
					: e?.message || "Failed to delete file",
			);
		}
	};

	const handleCopyLink = (doc) => {
		const link = doc?.fileUrl;
		if (link) {
			navigator.clipboard.writeText(link);
			message.success("Link copied to clipboard");
			return;
		}
		message.warning("Signed URL is not available for this file yet.");
	};

	const resetLinkModal = () => {
		setLinkModalOpen(false);
		setLinkUrlInput("");
		linkRangeRef.current = null;
		linkEditorElRef.current = null;
		linkNodeRef.current = null;
	};

	const handleLinkModalOk = () => {
		const url = linkUrlInput.trim();
		if (!url) {
			message.warning("Enter a URL");
			return false;
		}
		const existingLinkNode = linkNodeRef.current;
		if (existingLinkNode) {
			existingLinkNode.setAttribute("href", url);
			resetLinkModal();
			return true;
		}
		const range = linkRangeRef.current;
		const el = linkEditorElRef.current;
		if (!range || !el) {
			resetLinkModal();
			return true;
		}
		el.focus();
		const sel = window.getSelection();
		if (sel) {
			sel.removeAllRanges();
			sel.addRange(range);
		}
		document.execCommand("createLink", false, url);
		resetLinkModal();
		return true;
	};

	const BtnInsertLink = useMemo(
		() =>
			createButton("Insert link", "🔗", (editorState) => {
				const sel = window.getSelection();
				if (!sel || sel.rangeCount === 0) {
					message.warning("Select text to add a link");
					return;
				}
				const range = sel.getRangeAt(0);
				const anchorContainer =
					sel.anchorNode?.nodeType === Node.ELEMENT_NODE
						? sel.anchorNode
						: sel.anchorNode?.parentElement;
				const focusContainer =
					sel.focusNode?.nodeType === Node.ELEMENT_NODE
						? sel.focusNode
						: sel.focusNode?.parentElement;
				const editorEl = editorState.$el ?? null;
				const existingAnchor =
					anchorContainer?.closest?.("a[href]") ||
					focusContainer?.closest?.("a[href]") ||
					(range.commonAncestorContainer?.nodeType === Node.ELEMENT_NODE
						? range.commonAncestorContainer.closest?.("a[href]")
						: range.commonAncestorContainer?.parentElement?.closest?.(
							"a[href]",
						));
				const normalizedAnchor =
					existingAnchor && editorEl?.contains(existingAnchor)
						? existingAnchor
						: null;
				if (sel.isCollapsed && !normalizedAnchor) {
					message.warning("Select text to add a link");
					return;
				}
				linkRangeRef.current = range.cloneRange();
				linkEditorElRef.current = editorEl;
				linkNodeRef.current = normalizedAnchor;
				setLinkUrlInput(normalizedAnchor?.getAttribute("href") || "");
				setLinkModalOpen(true);
			}),
		[],
	);

	const EditorToolbar = () => (
		<Toolbar>
			<BtnBold />
			<BtnItalic />
			<BtnUnderline />
			<HtmlButton />
			<BtnInsertLink />
			<BtnBulletList />
			<BtnNumberedList />
			<BtnStrikeThrough />
			<BtnAlignCenter />
			<BtnAlignLeft />
			<BtnAlignRight />
			<BtnSuperscript />
			<BtnSubscript />

		</Toolbar>
	);

	useEffect(() => {
		if (!updateTermsAndConditionsState) return;
		if (updateTermsAndConditionsState?.message) {
			message.success(updateTermsAndConditionsState.message);
			navigate("/ai-campaigns/terms-conditions");
			dispatch(updateTermsAndConditionsAction.updateTermsAndConditionsInfoReset());
		}
		if (updateTermsAndConditionsState?.error) {
			message.error(updateTermsAndConditionsState.error);
			dispatch(updateTermsAndConditionsAction.updateTermsAndConditionsInfoReset());
		}
	}, [dispatch, updateTermsAndConditionsState, navigate]);

	const infoContent = (
		<div style={{ maxWidth: "660px" }}>
			Use only the supported variable format for placeholders:
			<br />
			%userName%, %dateOfBirth%, %memberId%, %signature%, %date%, %day%,
			%month%, %year%.
			{" "}These variables will be automatically replaced with the corresponding values when the template is generated.
		</div>
	);


	return (
		<>
			{getTermsAndConditionsDetailsState?.isLoading === true && (
				<Loader loaderTransform="loaderTransform" />
			)}
			<div className="rightside">
				<div className='d-flex'>
					<div className="rightcontent" >
						<p>
							<strong>Terms & Conditions</strong>
						</p>
						<CustomBreadcrumb
							items={[
								{ text: "Home" },
								{ text: "AI Campaigns", onClick: () => navigate("/ai-campaigns/campaign-list") },
								{ text: "Terms & Conditions", onClick: () => navigate("/ai-campaigns/terms-conditions") },
								{ text: "Add New Document", active: true },
							]} />
						<div className="d-flex justify-content-end mb-3">
							<div className="switches-container mb-30">
								<div className="mempro" onClick={() => setManageSwitch(false)}>
									English
								</div>
								<div className="mempro" onClick={() => setManageSwitch(true)}>
									Spanish
								</div>
								<div
									className={!manageSwitch ? "switch-wrapper" : "switch-wrapper switchside"}
								>
									<div className="switch">
										{!manageSwitch ? <div>English</div> : <div>Spanish</div>}
									</div>
								</div>
							</div>
						</div>
						<div className="table-padding tabbing mb-30 position-relative tabsetting">

							<Popover
								content={infoContent}
								trigger="click"
								open={infoPopoverVisible}
								onOpenChange={setInfoPopoverVisible}
								overlayClassName="custom-info-popover"
								placement="bottomRight"
							>
								<div className="infoiconpoistion ">
									<img src={infoicon} alt="Info" />
								</div>
							</Popover>
							<Tabs activeKey={activeKey} onSelect={(k) => setActiveKey(k || "text")} className="p-0">
								<Tab eventKey="text" title="HTML Editor">
									<div>
										<EditorProvider>
											<Editor
												value={currentHtml}
												onChange={onEditorChange}
												className="editorContent"
												disabled={getTermsAndConditionsDetailsState?.isLoading === true}
											>
												<EditorToolbar />
											</Editor>
										</EditorProvider>
										<div className="mt-3 d-flex justify-content-between textgrey w-100">
											<div className="fs-16">
												Note: {"</>"} Click this button before adding HTML text.
											</div>
										</div>
									</div>
								</Tab>
							</Tabs>
						</div>

						<div className="d-flex justify-content-end align-items-center gap-16">
							<button
								type="button"
								className="cancelbtn primarybtn w130 fs-18 "
								onClick={() => navigate("/ai-campaigns/terms-conditions")}
							>
								Cancel
							</button>
							<button
								type="button"
								className=" primarybtn fs-18 w130"
								onClick={handleSave}
								disabled={
									updateTermsAndConditionsState?.isLoading === true ||
									getTermsAndConditionsDetailsState?.isLoading === true
								}
							>
								{updateTermsAndConditionsState?.isLoading ? "Saving..." : "Save"}
							</button>
						</div>
					</div>
					<div className="document-library-sidebar">
						<div className="document-library-title fs-18 fw-700 mb-25">Document Library</div>
						<div className='mb-25'>
							<CsvUploadDragger
								accept=".pdf"
								uploadText={<span>Click here or drag & drop to upload <b>.pdf</b> file</span>}
								onFileSelected={handleFileSelected}
								showFileInside={false}
								multiple={true} />
						</div>

						<div className="pdf-list-container">
							<div className="uploaded-files-grid">
								{libraryDocuments.map((doc) => (
									<div key={doc.id} className="uploaded-file-card">
										<div className="file-info">
											<div className="file-icon-wrapper">
												<UploadCsvIcon color="white" />
											</div>
											<div className="file-name-text" title={doc.name}>{doc.name}</div>
										</div>
										<div className="file-actions">
											<button className="action-btn" onClick={() => handleCopyLink(doc)} title="Copy Link">
												<img src={copyIcon} alt="Copy" width="20" />
											</button>
											<button className="action-btn" onClick={() => setDeleteModal({ open: true, docId: doc.id })} title="Delete">
												<DeleteUploadIcon />
											</button>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
			<Modal
				title="Insert link"
				open={linkModalOpen}
				wrapClassName="insert-link-modal"
				okText="Apply"
				okButtonProps={{ className: "primarybtn fs-18 w130" }}
				cancelButtonProps={{ className: "cancelbtn primarybtn w130 fs-18" }}
				onOk={handleLinkModalOk}
				onCancel={resetLinkModal}
				destroyOnClose
			>
				<Input
					placeholder="https://example.com"
					value={linkUrlInput}
					onChange={(e) => setLinkUrlInput(e.target.value)}
					onPressEnter={handleLinkModalOk}
					autoFocus
				/>
			</Modal>
			<CampaignModal
				open={deleteModal.open}
				onCancel={closeDeleteModal}
				title="Delete PDF"
				subtitle="Removing this PDF will delete the file from storage and stop it from being used in the campaign."
				actionLabel={deleteFileState?.isLoading ? "Deleting..." : "Delete"}
				actionDisabled={deleteFileState?.isLoading === true}
				onAction={handleDeleteDoc}
			>
				<div className="campaign-modal-note-box">
					<div className="d-flex align-items-start gap-16 flex-column">
						<div className="d-flex align-items-center gap-2 justify-content-center">
							<InfoIcon />
							<span className="fw-bold fs-14" style={{ color: "#162C4E" }}>This action will:</span>
						</div>
						<span className="fs-14">
							Require manual removal of the PDF link from the HTML editor
						</span>
					</div>
				</div>
				<hr className="my-3" />
			</CampaignModal>
		</>
	)
}

export default ManageTerms
